import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useStore } from '../store/useStore';
import { DiagnosticEngine, DiagnosticIssue, DiagnosticSuggestion } from '../services/diagnosticEngine';
import { formatDate } from '../lib/dateUtils';
import { AlertTriangle, AlertCircle, RefreshCw, Layers } from 'lucide-react';

export const F10CenterView = ({ handleItemSelect }: { handleItemSelect: (item: any) => void }) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const toDate = useStore((state) => state.toDate);

  const [issues, setIssues] = useState<DiagnosticIssue[]>([]);
  const [suggestions, setSuggestions] = useState<DiagnosticSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [isScanningFull, setIsScanningFull] = useState(false);
  const [scanMode, setScanMode] = useState<'recent' | 'full'>('recent');
  const [lastScan, setLastScan] = useState<string | null>(null);
  const fullScanCache = useRef<{ ts: number, issues: DiagnosticIssue[], suggestions: DiagnosticSuggestion[] } | null>(null);

  const triggerScan = async (mode: 'recent' | 'full') => {
    if (!activeCompany) return;
    
    if (mode === 'full') {
      const now = Date.now();
      if (fullScanCache.current && (now - fullScanCache.current.ts < 60000)) {
         // Use cached version if within 60 seconds
         setIssues(fullScanCache.current.issues);
         setSuggestions(fullScanCache.current.suggestions);
         setScanMode('full');
         setLastScan(`Last full scan: ${new Date(fullScanCache.current.ts).toLocaleTimeString()}`);
         return;
      }
      setIsScanningFull(true);
    } else {
      setLoading(true);
    }

    try {
      const { issues: resIssues, suggestions: resSugg } = await DiagnosticEngine.runAllDiagnostics(activeCompany.id!, toDate, mode);
      setIssues(resIssues);
      setSuggestions(resSugg);
      setScanMode(mode);
      setLastScan(mode === 'full' 
        ? `Last full scan: ${new Date().toLocaleTimeString()}`
        : `Last recent scan: ${new Date().toLocaleTimeString()}`);
        
      if (mode === 'full') {
         fullScanCache.current = { ts: Date.now(), issues: resIssues, suggestions: resSugg };
      }
    } catch (err: any) {
      setIssues([{ id: 'err_scan', type: 'HighValue', title: 'Diagnostic Error', description: err?.message || 'An unexpected error occurred during the scan.', severity: 'Critical', timestamp: new Date().toISOString() }]);
    } finally {
      setIsScanningFull(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    triggerScan('recent');
  }, [activeCompany, toDate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key.toLowerCase() === 'q') {
        e.preventDefault();
        handleItemSelect({ name: 'Quit' });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleItemSelect]);

  const summary = useMemo(() => {
    return {
      total: issues.length,
      critical: issues.filter(i => i.severity === 'Critical').length,
      warning: issues.filter(i => i.severity === 'Warning').length
    };
  }, [issues]);

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      {/* Header */}
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10 text-[13px]">
        <span className="w-1/3 text-left">Issues &amp; Suggestions Center [F10]</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      {loading ? (
        <div className="flex-1 flex flex-col items-center justify-center text-[#1e8585]">
           <RefreshCw className="w-8 h-8 animate-spin mb-4" />
           <span className="font-bold text-[14px]">Running Diagnostics Engine...</span>
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden bg-gray-50 p-4 gap-4">
          
          {/* Main Content */}
          <div className="flex flex-col flex-1 border border-gray-300 bg-white shadow-sm overflow-hidden rounded">
            {/* Summary Panel */}
            <div className="flex flex-col border-b border-gray-200 bg-gray-100">
               <div className="flex items-center justify-between p-3">
                 <div className="flex gap-6">
                    <div className="flex flex-col">
                       <span className="text-[10px] text-gray-500 font-bold uppercase">Total Issues</span>
                       <span className="text-[18px] font-bold text-[#1e8585]">{summary.total}</span>
                    </div>
                    <div className="flex flex-col">
                       <span className="text-[10px] text-gray-500 font-bold uppercase">Critical</span>
                       <span className="text-[18px] font-bold text-red-600">{summary.critical}</span>
                    </div>
                    <div className="flex flex-col">
                       <span className="text-[10px] text-gray-500 font-bold uppercase">Warnings</span>
                       <span className="text-[18px] font-bold text-amber-600">{summary.warning}</span>
                    </div>
                 </div>
                 <div className="flex flex-col items-end">
                    <span className="text-[10px] text-gray-500 font-bold uppercase">{lastScan || '-'}</span>
                    <button 
                       onClick={() => triggerScan('full')}
                       disabled={isScanningFull}
                       className="mt-1 text-[10px] text-[#1e8585] hover:underline flex items-center gap-1 disabled:opacity-50"
                    >
                       <RefreshCw className={`w-3 h-3 ${isScanningFull ? 'animate-spin' : ''}`} /> 
                       {isScanningFull ? 'Scanning full data...' : 'Rescan Full Data'}
                    </button>
                 </div>
               </div>
               <div className="bg-[#eefcfc] px-3 py-1.5 text-[11px] text-[#1e8585] border-t border-[#ccecec] font-medium flex items-center gap-2">
                 {isScanningFull ? (
                    <><RefreshCw className="w-3 h-3 animate-spin"/> Scanning full data... Please wait, you can continue exploring.</>
                 ) : scanMode === 'recent' ? (
                    <span>Showing recent analysis (last 100 entries)</span>
                 ) : (
                    <span>Showing comprehensive full database analysis</span>
                 )}
               </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto scrollbar-thin p-1">
               {issues.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                     <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-2">
                        <Layers className="text-green-500 w-6 h-6" />
                     </div>
                     <span className="font-bold text-[14px]">No issues detected</span>
                     <span className="text-[11px]">System is healthy and optimized.</span>
                  </div>
               ) : (
                  <div className="flex flex-col gap-1 p-2">
                     {issues.map(issue => (
                        <div key={issue.id} className="flex flex-col p-3 border border-gray-200 hover:bg-zinc-50 rounded group">
                           <div className="flex items-center gap-2 mb-1">
                              {issue.severity === 'Critical' ? 
                                 <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" /> : 
                                 <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              }
                              <span className={`font-bold uppercase text-[10px] ${issue.severity === 'Critical' ? 'text-red-700' : 'text-amber-700'}`}>
                                 {issue.type}
                              </span>
                              <span className="text-gray-400 text-[10px] ml-auto">{formatDate(issue.timestamp)}</span>
                           </div>
                           <span className="font-bold text-[13px] text-gray-800">{issue.title}</span>
                           <p className="text-gray-600 mt-1 leading-relaxed">{issue.description}</p>
                        </div>
                     ))}
                  </div>
               )}
            </div>
          </div>

          {/* Suggestions Panel */}
          <div className="w-[280px] border border-gray-300 bg-white shadow-sm overflow-hidden flex flex-col rounded">
             <div className="p-2 border-b border-gray-200 bg-[#edf5f5]">
                <span className="font-bold text-[#1e8585] uppercase text-[11px]">Smart Suggestions</span>
             </div>
             <div className="p-2 flex flex-col overflow-y-auto scrollbar-thin flex-1 text-gray-700 bg-gray-50/50">
                {suggestions.length === 0 ? (
                  <div className="flex flex-col items-center p-4 text-center text-gray-400">
                    <Layers className="w-8 h-8 text-gray-300 mb-2" />
                    <span className="font-semibold text-[13px]">Analyzing Patterns...</span>
                    <span className="text-[11px] mt-2 italic px-2">Suggestions engine is collecting data. Check back later for automated workflow recommendations.</span>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                     {suggestions.map(s => (
                        <div key={s.id} className="p-3 border border-[#aebec8] bg-white rounded shadow-sm hover:shadow-md transition-shadow cursor-default">
                           <span className="font-bold text-[#1e8585] text-[11px] mb-1 flex items-center gap-1"><Layers className="w-3 h-3"/> {s.title}</span>
                           <p className="text-[10px] leading-relaxed text-gray-600">{s.description}</p>
                        </div>
                     ))}
                  </div>
                )}
             </div>
          </div>

        </div>
      )}

      {/* Shortcuts bar */}
      <div className="flex border-t border-[#aebec8] bg-white h-[22px] w-full shadow-inner mt-auto text-[11px]">
        {[
          { key: 'Q', label: 'Quit', action: () => handleItemSelect({ name: 'Quit' }) }
        ].map((sc, i) => (
          <div key={i} className="flex-1 border-r border-[#aebec8] flex justify-between items-center px-1 cursor-pointer hover:bg-gray-100"
            onClick={sc.action}>
            <div className="flex items-center space-x-[2px] whitespace-nowrap">
              <span className="text-[#2a62a3] font-bold underline decoration-[#2a62a3] underline-offset-2">{sc.key[0]}</span>
              {sc.key.length > 1 && <span className="text-[#2a62a3] font-bold">{sc.key.substring(1)}</span>}
              <span className="text-[#2a62a3] font-bold">:</span>
              <span className="text-black leading-none">{sc.label}</span>
            </div>
            <span className="text-[#aebec8] text-[9px] mb-1">^</span>
          </div>
        ))}
      </div>
    </div>
  );
};
